import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { TokensResponse, ValidateOtpResponse } from "pages/authentication/authenticationService";
import { LoanStatusType } from "pages/dashboard/home/dashboardService";
import { LeadProvider, RegistrationRoleType } from "pages/registration/registrationService";
import type { RootState } from "store";
import { ACCESS_TOKEN_EXPIRATION_MINUTES } from "utilities/Constant";
import { addMinutesToCurrentDate } from "utilities/date";
import { applicationApi } from "pages/application/applicationService";

export interface UserState {
  userName: string;
  accessToken: string;
  userId: string;
  applicationId: string | undefined;
  status: string | undefined;
  tokenValidationParams: TokenValidation;
  version: string;
  isRedesignAnnouncementVisible: boolean;
  dealApplicationId: string | undefined;
  nhDealApplicationId: string | undefined;
  prospectId: string | undefined;
  applicationRoleType: string;
  isLogin: boolean;
  loanStatus?: LoanStatusType;
  leadProvider: LeadProvider;
  loanPurpose: string;
  nhApplicationStatus: string;
  hasPAL: boolean | undefined;
  isInitialDocUploaded: boolean | undefined;
  currentTooltipIndex: number;
  loanId: string | undefined;
  selfReferralInSession: boolean;
  hasInterimDashboard: boolean;
  isAlertVisible: boolean;
  isCloseAttentionBanner: boolean;
  partnerAdId: string;
  isConsentCollected: boolean;
  userRole: RegistrationRoleType;
  inquiryId?: string;
  hasESign: boolean | undefined;
  emailAddress?: string;
  coBorrowerHasOwnNhApplication: boolean;
  nhApplicationPath: string;
  activeTab: string;
  showProfile: boolean;
  previousTab: string;
  selectedTaskId?: string;
  taskFilter: TaskFilterType;
  taskCount: number;
  isInitialUqualReferral: boolean;
  isEsignTaskViewed: boolean;
  isLoanDetailsUpdateViewed: boolean;
  loanNumber: string;
  isImpersonating: boolean;
  applicationNumber: string;
  displayMyLoans: boolean;
  myLoansFetchedAt: number | null;
  myLoansStatus: MyLoansStatus;
  encompassLoanId?: string;
  dealId?: string;
  preferredLanguage?: string;
  dealBorrowerId?: string;
  hasPreferredLanguageOverride: boolean;
  hasAcknowledgedSpanishPrompt: boolean;
}

interface TokenValidation {
  refreshToken: string;
  lastActiveTime: Date;
  tokenExpiration: Date;
}

interface ApplicationRoutingData {
  nhDealApplicationId: string;
  nhApplicationId: string;
  applicationRoleType: string;
  dealApplicationId: string;
  nhApplicationStatus: string;
  loanId?: string;
  hasPal?: boolean;
  prospectId?: string;
  leadProvider: LeadProvider;
  loanPurpose: string;
  isConsentCollected: boolean;
  userRole: RegistrationRoleType;
  inquiryId?: string;
  coBorrowerHasOwnNhApplication: boolean;
  nhApplicationPath: string;
  loanNumber: string;
  applicationNumber: string;
  encompassLoanId?: string;
  dealId?: string;
  dealBorrowerId?: string;
}

export type MyLoansStatus = "idle" | "pending" | "fulfilled" | "rejected";

export interface AppState {
  activeTab: string;
  showProfile: boolean;
  previousTab: string;
}

export interface AppState {
  activeTab: string;
  showProfile: boolean;
  previousTab: string;
}

export enum TaskFilterType {
  ToDo = "todo",
  UnderReview = "underReview",
  Complete = "complete",
}

const initialState: UserState = {
  userName: "",
  accessToken: "",
  userId: "",
  applicationId: "",
  status: "",
  tokenValidationParams: {
    refreshToken: "",
    lastActiveTime: new Date(0),
    tokenExpiration: new Date(0),
  },
  version: "",
  dealApplicationId: "",
  nhDealApplicationId: "",
  prospectId: "",
  applicationRoleType: "",
  isLogin: false,
  loanStatus: "Started",
  leadProvider: LeadProvider.Default,
  loanPurpose: "",
  nhApplicationStatus: "",
  hasPAL: false,
  isInitialDocUploaded: false,
  currentTooltipIndex: 0,
  loanId: "",
  selfReferralInSession: false,
  hasInterimDashboard: false,
  partnerAdId: "",
  isAlertVisible: false,
  isCloseAttentionBanner: false,
  isConsentCollected: false,
  userRole: RegistrationRoleType.Unknown,
  inquiryId: "",
  hasESign: false,
  emailAddress: "",
  coBorrowerHasOwnNhApplication: false,
  nhApplicationPath: "",
  activeTab: "home",
  showProfile: false,
  previousTab: "home",
  selectedTaskId: undefined,
  taskFilter: TaskFilterType.ToDo,
  taskCount: 0,
  isInitialUqualReferral: false,
  isEsignTaskViewed: false,
  isLoanDetailsUpdateViewed: false,
  isRedesignAnnouncementVisible: false,
  loanNumber: "",
  applicationNumber: "",
  displayMyLoans: false,
  myLoansFetchedAt: null,
  myLoansStatus: "idle",
  isImpersonating: false,
  encompassLoanId: "",
  dealId: "",
  preferredLanguage: undefined,
  dealBorrowerId: undefined,
  hasPreferredLanguageOverride: false,
  hasAcknowledgedSpanishPrompt: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<ValidateOtpResponse>) => {
      const { name, accessToken, id, refreshToken, version, isRedesignAnnouncementVisible } = action.payload;
      const dateTimeStamp = new Date();
      state.userName = name;
      state.accessToken = accessToken;
      state.userId = id;
      state.tokenValidationParams.refreshToken = refreshToken;
      state.tokenValidationParams.lastActiveTime = dateTimeStamp;
      state.tokenValidationParams.tokenExpiration = addMinutesToCurrentDate(dateTimeStamp, ACCESS_TOKEN_EXPIRATION_MINUTES);
      state.version = version;
      state.isRedesignAnnouncementVisible = isRedesignAnnouncementVisible ?? false;
    },
    setLogout: () => initialState,
    setApplicationId: (state, action: PayloadAction<string>) => {
      state.applicationId = action.payload;
    },
    setNhDealApplicationId: (state, action: PayloadAction<string>) => {
      state.nhDealApplicationId = action.payload;
    },
    setNhApplicationStatus: (state, action: PayloadAction<string>) => {
      state.nhApplicationStatus = action.payload;
    },
    setTokens: (state, action: PayloadAction<TokensResponse>) => {
      state.accessToken = action.payload.accessToken;
      state.tokenValidationParams.refreshToken = action.payload.refreshToken;
      state.tokenValidationParams.tokenExpiration = action.payload.tokenExpireTime;
      state.tokenValidationParams.lastActiveTime = new Date();
    },
    setLastActiveSession: (state, action: PayloadAction<Date>) => {
      state.tokenValidationParams.lastActiveTime = action.payload;
    },
    setUserId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    setIsLogin: (state, action: PayloadAction<boolean>) => {
      state.isLogin = action.payload;
    },
    setIsInitialDocUploaded: (state, action: PayloadAction<boolean>) => {
      state.isInitialDocUploaded = action.payload;
    },
    setLeadProvider: (state, action: PayloadAction<LeadProvider>) => {
      state.leadProvider = action.payload;
    },
    setCurrentTooltipIndex: (state, action: PayloadAction<number>) => {
      state.currentTooltipIndex = action.payload;
    },
    setApplicationRoutingData: (state, action: PayloadAction<ApplicationRoutingData>) => {
      const {
        nhDealApplicationId,
        nhApplicationId,
        applicationRoleType,
        dealApplicationId,
        nhApplicationStatus,
        loanId,
        hasPal,
        prospectId,
        loanPurpose,
        leadProvider,
        isConsentCollected,
        userRole,
        inquiryId,
        coBorrowerHasOwnNhApplication,
        nhApplicationPath,
        loanNumber,
        applicationNumber,
        encompassLoanId,
        dealId,
        dealBorrowerId,
      } = action.payload;
      state.nhDealApplicationId = nhDealApplicationId;
      state.applicationId = nhApplicationId;
      state.applicationRoleType = applicationRoleType;
      state.dealApplicationId = dealApplicationId;
      state.nhApplicationStatus = nhApplicationStatus;
      state.loanId = loanId;
      state.hasPAL = hasPal;
      state.prospectId = prospectId;
      state.loanPurpose = loanPurpose;
      state.leadProvider = leadProvider;
      state.isConsentCollected = isConsentCollected;
      state.userRole = userRole;
      state.inquiryId = inquiryId;
      state.coBorrowerHasOwnNhApplication = coBorrowerHasOwnNhApplication;
      state.nhApplicationPath = nhApplicationPath;
      state.loanNumber = loanNumber;
      state.applicationNumber = applicationNumber;
      state.encompassLoanId = encompassLoanId;
      state.dealId = dealId;
      state.dealBorrowerId = dealBorrowerId;
    },
    setSelfReferralInSession: (state, action: PayloadAction<boolean>) => {
      state.selfReferralInSession = action.payload;
    },
    setIsInitialUqualReferral: (state, action: PayloadAction<boolean>) => {
      state.isInitialUqualReferral = action.payload;
    },
    setIsInterimDashboard: (state, action: PayloadAction<boolean>) => {
      state.hasInterimDashboard = action.payload;
    },
    setIsAlertVisibility: (state, action: PayloadAction<boolean>) => {
      state.isAlertVisible = action.payload;
    },
    setCloseAttentionBanner: (state, action: PayloadAction<boolean>) => {
      state.isCloseAttentionBanner = action.payload;
    },
    setPartnerSession: (state, action: PayloadAction<string>) => {
      state.partnerAdId = action.payload;
    },
    setHasESign: (state, action: PayloadAction<boolean>) => {
      state.hasESign = action.payload;
    },
    setEmailAddress: (state, action: PayloadAction<string>) => {
      state.emailAddress = action.payload;
    },
    setIsConsentCollected: (state, action: PayloadAction<boolean>) => {
      state.isConsentCollected = action.payload;
    },
    setProfileActiveTab: (state, action: PayloadAction<AppState["activeTab"]>) => {
      state.activeTab = action.payload;
      state.previousTab = action.payload;
      state.showProfile = false; // ensure profile closes when navigating
    },
    openProfile: (state) => {
      state.showProfile = true;
    },
    closeProfile: (state) => {
      state.showProfile = false;
      state.activeTab = state.previousTab; // Go back to where user was
    },
    setSelectedTaskId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedTaskId = action.payload;
    },
    setTaskFilter: (state, action: PayloadAction<TaskFilterType>) => {
      state.taskFilter = action.payload;
    },
    setTaskCount: (state, action: PayloadAction<number>) => {
      // Used to set the Tasks badge in shared navigation
      state.taskCount = action.payload;
    },
    setNhApplicationPath: (state, action: PayloadAction<string>) => {
      state.nhApplicationPath = action.payload;
    },
    setIsEsignTaskViewed: (state, action: PayloadAction<boolean>) => {
      state.isEsignTaskViewed = action.payload;
    },
    setIsLoanDetailsUpdateViewed: (state, action: PayloadAction<boolean>) => {
      state.isLoanDetailsUpdateViewed = action.payload;
    },
    setIsRedesignAnnouncementVisible: (state, action: PayloadAction<boolean>) => {
      state.isRedesignAnnouncementVisible = action.payload;
    },
    setIsImpersonating: (state, action: PayloadAction<boolean>) => {
      state.isImpersonating = action.payload;
    },
    setPreferredLanguage: (state, action: PayloadAction<string | undefined>) => {
      state.preferredLanguage = action.payload;
    },
    setHasPreferredLanguageOverride: (state, action: PayloadAction<boolean>) => {
      state.hasPreferredLanguageOverride = action.payload;
    },
    setHasAcknowledgedSpanishPrompt: (state, action: PayloadAction<boolean>) => {
      state.hasAcknowledgedSpanishPrompt = action.payload;
    },
    setDealBorrowerId: (state, action: PayloadAction<string | undefined>) => {
      state.dealBorrowerId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(applicationApi.endpoints.getMyLoans.matchPending, (state) => {
        state.myLoansStatus = "pending";
      })
      .addMatcher(applicationApi.endpoints.getMyLoans.matchFulfilled, (state, action) => {
        const count = (action.payload?.length ?? 0);
        state.displayMyLoans = count > 1;
        state.myLoansFetchedAt = Date.now();
        state.myLoansStatus = "fulfilled";
      })
      .addMatcher(applicationApi.endpoints.getMyLoans.matchRejected, (state, action) => {
        state.myLoansStatus = "rejected";
        state.myLoansFetchedAt = Date.now();
        state.displayMyLoans = false;
      });
  },
});

export const {
  setCredentials,
  setLogout,
  setApplicationId,
  setTokens,
  setLastActiveSession,
  setUserId,
  setIsLogin,
  setNhDealApplicationId,
  setLeadProvider,
  setApplicationRoutingData,
  setCurrentTooltipIndex,
  setNhApplicationStatus,
  setIsInitialDocUploaded,
  setSelfReferralInSession,
  setIsInterimDashboard,
  setIsAlertVisibility,
  setCloseAttentionBanner,
  setPartnerSession,
  setIsConsentCollected,
  setHasESign,
  setEmailAddress,
  setProfileActiveTab,
  openProfile,
  closeProfile,
  setSelectedTaskId,
  setTaskFilter,
  setTaskCount,
  setIsInitialUqualReferral,
  setNhApplicationPath,
  setIsEsignTaskViewed,
  setIsLoanDetailsUpdateViewed,
  setIsRedesignAnnouncementVisible,
  setIsImpersonating,
  setPreferredLanguage,
  setHasPreferredLanguageOverride,
  setHasAcknowledgedSpanishPrompt,
  setDealBorrowerId,
} = authSlice.actions;

export const getCurrentUserName = (state: RootState): string => state.auth.userName;
export const getCurrentAccessToken = (state: RootState): string => state.auth.accessToken;
export const getCurrentUserId = (state: RootState): string => state.auth.userId;
export const getCurrentUserApplicationId = (state: RootState): string => state.auth.applicationId ?? "";
export const getCurrentUserApplicationStatus = (state: RootState): string => state.auth.status ?? "";
export const getTokenValidationParameters = (state: RootState): TokenValidation => state.auth.tokenValidationParams;
export const getVersion = (state: RootState): string => state.auth.version;
export const getDealApplicationId = (state: RootState): string => state.auth.dealApplicationId ?? "";
export const getLoanId = (state: RootState): string => state.auth.loanId ?? "";
export const getProspectId = (state: RootState): string => state.auth.prospectId ?? "";
export const getUserRole = (state: RootState): string => state.auth.userRole ?? "";
export const getLoanPurpose = (state: RootState): string => state.auth.loanPurpose ?? "";
export const getLeadProvider = (state: RootState): string => state.auth.leadProvider;
export const getNhDealApplicationId = (state: RootState): string => state.auth.nhDealApplicationId ?? "";
export const getInquiryId = (state: RootState): string => state.auth.inquiryId ?? "";
export const getHasPAL = (state: RootState): boolean => state.auth.hasPAL ?? false;
export const getIsInitialDocUploaded = (state: RootState): boolean => state.auth.isInitialDocUploaded ?? false;
export const getCurrentTooltipIndex = (state: RootState): number => state.auth.currentTooltipIndex;
export const getSelfReferralInSession = (state: RootState): boolean => state.auth.selfReferralInSession;
export const getIsInterimDashboard = (state: RootState): boolean => state.auth.hasInterimDashboard;
export const getIsAlertVisibility = (state: RootState): boolean => state.auth.isAlertVisible;
export const getCloseAttentionBanner = (state: RootState): boolean => state.auth.isCloseAttentionBanner;
export const getPartnerName = (state: RootState): string => state.auth.partnerAdId ?? "";
export const getHasESign = (state: RootState): boolean => state.auth.hasESign ?? false;
export const getEmailAddress = (state: RootState): string => state.auth.emailAddress ?? "";
export const getIsConsentCollected = (state: RootState): boolean => state.auth.isConsentCollected ?? false;
export const getIsCoBorrowerHasOwnNhApplication = (state: RootState): boolean => state.auth.coBorrowerHasOwnNhApplication;
export const getNhApplicationPath = (state: RootState): string => state.auth.nhApplicationPath || "FullPath";
export const getNhApplicationStatus = (state: RootState): string => state.auth.nhApplicationStatus;
export const getApplicationRoleType = (state: RootState): string => state.auth.applicationRoleType ?? "";
export const getActiveTab = (state: RootState): string => state.auth.activeTab ?? "";
export const getPreviousTab = (state: RootState): string => state.auth.previousTab ?? "";
export const getProfileSettings = (state: RootState): boolean => state.auth.showProfile;
export const getSelectedTaskId = (state: RootState): string | undefined => state.auth.selectedTaskId;
export const getTaskFilter = (state: RootState): TaskFilterType => state.auth.taskFilter;
export const getTaskCount = (state: RootState): number => state.auth.taskCount;
export const getIsInitialUqualReferral = (state: RootState): boolean => state.auth.isInitialUqualReferral;
export const getIsEsignTaskViewed = (state: RootState): boolean => state.auth.isEsignTaskViewed;
export const getIsLoanDetailsUpdateViewed = (state: RootState): boolean => state.auth.isLoanDetailsUpdateViewed;
export const getIsRedesignAnnouncementVisible = (state: RootState): boolean => state.auth.isRedesignAnnouncementVisible;
export const getLoanNumber = (state: RootState): string => state.auth.loanNumber;
export const getIsImpersonating = (state: RootState): boolean => state.auth.isImpersonating;
export const getDisplayMyLoans = (state: RootState): boolean => state.auth.displayMyLoans;
export const getMyLoansFetchedAt = (state: RootState): number | null => state.auth.myLoansFetchedAt;
export const getMyLoansStatus = (state: RootState): MyLoansStatus => state.auth.myLoansStatus;
export const getEncompassLoanId = (state: RootState): string | undefined => state.auth.encompassLoanId;
export const getDealId = (state: RootState): string | undefined => state.auth.dealId;
export const selectPreferredLanguage = (state: RootState): string | undefined => state.auth.preferredLanguage;
export const getHasPreferredLanguageOverride = (state: RootState): boolean => state.auth.hasPreferredLanguageOverride;
export const getHasAcknowledgedSpanishPrompt = (state: RootState): boolean => state.auth.hasAcknowledgedSpanishPrompt;
export const getDealBorrowerId = (state: RootState): string | undefined => state.auth.dealBorrowerId;
export default authSlice.reducer;
